import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { mediaQueryLarge } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#initializeGrid();
  }

  #controller = new AbortController();
  #mediaGrid = /** @type {HTMLElement | null} */ (null);
  #productDetails = /** @type {HTMLElement | null} */ (null);
  #viewMoreWrapper = /** @type {HTMLElement | null} */ (null);
  #resizeObserver = /** @type {ResizeObserver | null} */ (null);
  #fillFrame = /** @type {number | null} */ (null);

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#resizeObserver?.disconnect();

    if (this.#fillFrame !== null) {
      cancelAnimationFrame(this.#fillFrame);
    }
  }

  #initializeGrid() {
    if (this.presentation !== 'grid') return;

    this.#mediaGrid = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-testid="media-gallery-grid"]')
    );
    this.#viewMoreWrapper = /** @type {HTMLElement | null} */ (
      this.querySelector('.product-media-gallery__view-more-wrapper')
    );
    this.#productDetails = /** @type {HTMLElement | null} */ (
      this.closest('.product-information__grid')?.querySelector('[data-testid="product-information-details"]') ?? null
    );

    if (!this.#mediaGrid) return;

    const { signal } = this.#controller;
    const viewMoreButton = this.#viewMoreWrapper?.querySelector('[data-view-more-button]');

    viewMoreButton?.addEventListener('click', this.#handleViewMore, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange, { signal });

    this.#mediaGrid.querySelectorAll('img').forEach((image) => {
      if (!image.complete) {
        image.addEventListener('load', this.#scheduleGalleryFill, { signal });
      }
    });

    if (this.#productDetails && 'ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(this.#scheduleGalleryFill);
      this.#resizeObserver.observe(this.#productDetails);
    }

    this.#scheduleGalleryFill();
  }

  #handleViewMore = () => {
    this.#revealHiddenMedia(4);
    this.#updateViewMoreVisibility();
  };

  /**
   * @param {MediaQueryListEvent} event
   */
  #handleBreakpointChange = (event) => {
    if (event.matches) {
      this.#scheduleGalleryFill();
    }
  };

  #scheduleGalleryFill = () => {
    if (!mediaQueryLarge.matches || this.#fillFrame !== null) return;

    this.#fillFrame = requestAnimationFrame(() => {
      this.#fillFrame = null;
      this.#fillGalleryToDetails();
    });
  };

  #fillGalleryToDetails() {
    if (!mediaQueryLarge.matches || !this.#mediaGrid || !this.#productDetails) return;

    const rowGap = parseFloat(getComputedStyle(this.#mediaGrid).rowGap) || 0;
    const tolerance = Math.max(rowGap, 1);
    const columnCount =
      getComputedStyle(this.#mediaGrid).gridTemplateColumns.split(/\s+/).filter(Boolean).length || 1;

    while (
      this.#getHiddenMedia().length > 0 &&
      this.getBoundingClientRect().height + tolerance < this.#productDetails.getBoundingClientRect().height
    ) {
      this.#revealHiddenMedia(columnCount);
    }

    this.#updateViewMoreVisibility();
  }

  /**
   * @param {number} count
   */
  #revealHiddenMedia(count) {
    this.#getHiddenMedia()
      .slice(0, count)
      .forEach((item) => {
        item.hidden = false;
        item.classList.remove('product-media-container--hidden');
      });
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getHiddenMedia() {
    return this.#mediaGrid
      ? Array.from(
          /** @type {NodeListOf<HTMLElement>} */ (
            this.#mediaGrid.querySelectorAll('.product-media-container--hidden')
          )
        )
      : [];
  }

  #updateViewMoreVisibility() {
    this.#viewMoreWrapper?.toggleAttribute('hidden', this.#getHiddenMedia().length === 0);
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
